/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { useState, useRef, useEffect } from "react";
import styles from "./users-list.module.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  updateCurrentlySelectedUserForEditUserDrawer,
  updateModalsCloseDrawerModal,
} from "@src/reducers/your-reducer";
import { IndicatorText } from "@src/components/atoms/indicator-text/indicator-text";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import { RootState } from "@src/reducers/store";
import { ZitadelUser } from "@src/types/user-management";
import { useI18N } from "@src/language/language-context";
import questionMarkIcon from "@src/assets/svg/question-mark-full-gray-12.svg";
import { RolesDescriptionPopover } from "@src/components/molecules/roles-description-popover/roles-description-popover";

interface UsersListProps {
  users: ZitadelUser[];
}

interface UsersListHeaderProps {
  columns: { key: string; label: string }[];
  columnWidths: { [key: string]: number };
  resizing: {
    handleKey: string;
    leftCol: string;
    rightCol: string;
    startX: number;
    startLeftWidth: number;
    startRightWidth: number;
  } | null;
  handleMouseDown: (
    e: React.MouseEvent<HTMLDivElement>,
    leftColKey: string,
    rightColKey: string,
  ) => void;
}

const cssClassName = "users-list";

const UsersListHeader = ({
  columns,
  columnWidths,
  resizing,
  handleMouseDown,
}: UsersListHeaderProps) => {
  const i18n = useI18N();
  const rolesDescriptions = useSelector(
    (state: RootState) => state.yourSlice.rolesDescriptions,
  );

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const texts = {
    rolesDescriptionPopoverAltText: i18n(
      "modules.usersList.rolesDescriptionPopoverAltText",
    ),
  };

  return (
    <thead>
      <tr>
        {columns.map((column, index) => (
          <th
            key={column.key}
            style={{ width: `${columnWidths[column.key]}%` }}
            className={styles[`${cssClassName}__header`]}
            onPointerLeave={() => {
              setIsPopoverOpen(false);
            }}
          >
            {index > 0 ? (
              <div
                className={`${
                  styles[`${cssClassName}__header-resize-handle`]
                } ${
                  resizing?.handleKey === column.key
                    ? styles[`${cssClassName}__header-resize-handle--dragging`]
                    : ""
                }`}
                onMouseDown={(e) =>
                  handleMouseDown(e, columns[index - 1].key, column.key)
                }
              />
            ) : (
              <div
                className={styles[`${cssClassName}__header-static-handle`]}
              />
            )}
            <div className={styles[`${cssClassName}__header-content`]}>
              <p>{column.label}</p>
              {Object.keys(rolesDescriptions).length && index === 5 && (
                <>
                  <img
                    src={questionMarkIcon}
                    alt={texts.rolesDescriptionPopoverAltText}
                    className={styles[`${cssClassName}__roles-icon`]}
                    onPointerEnter={() => {
                      setIsPopoverOpen(true);
                    }}
                  />

                  {isPopoverOpen && (
                    <RolesDescriptionPopover
                      rolesDescriptions={rolesDescriptions}
                    />
                  )}
                </>
              )}
            </div>
          </th>
        ))}
      </tr>
    </thead>
  );
};

export const UsersList = ({ users }: UsersListProps) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const language = useSelector((state: RootState) => state.yourSlice.language);

  const texts = {
    givenNameLabel: i18n("modules.usersList.givenNameLabel"),
    familyNameLabel: i18n("modules.usersList.familyNameLabel"),
    loginNameLabel: i18n("modules.usersList.loginNameLabel"),
    emailLabel: i18n("modules.usersList.emailLabel"),
    statusLabel: i18n("modules.usersList.statusLabel"),
    rolesLabel: i18n("modules.usersList.rolesLabel"),
    createdAtLabel: i18n("modules.usersList.createdAtLabel"),
    updatedAtLabel: i18n("modules.usersList.updatedAtLabel"),
    statusTextActive: i18n("modules.usersList.statusTextActive"),
    statusTextInactive: i18n("modules.usersList.statusTextInactive"),
    statusTextInitial: i18n("modules.usersList.statusTextInitial"),
    noUsersFoundMessage: i18n("modules.usersList.noUsersFoundMessage"),
  };

  const [columnWidths, setColumnWidths] = useState({
    givenName: 7,
    familyName: 7,
    preferred_login_name: 24,
    email: 20,
    status: 6,
    roles: 12,
    createdAt: 12,
    updatedAt: 12,
  });

  const [resizing, setResizing] = useState(null);
  const tableRef = useRef(null);

  const columns = [
    { key: "givenName", label: texts.givenNameLabel },
    { key: "familyName", label: texts.familyNameLabel },
    { key: "preferred_login_name", label: texts.loginNameLabel },
    { key: "email", label: texts.emailLabel },
    { key: "status", label: texts.statusLabel },
    { key: "roles", label: texts.rolesLabel },
    { key: "createdAt", label: texts.createdAtLabel },
    { key: "updatedAt", label: texts.updatedAtLabel },
  ];

  const handleMouseDown = (e, leftColKey, rightColKey) => {
    e.preventDefault();
    e.stopPropagation();

    setResizing({
      handleKey: rightColKey,
      leftCol: leftColKey,
      rightCol: rightColKey,
      startX: e.clientX,
      startLeftWidth: columnWidths[leftColKey],
      startRightWidth: columnWidths[rightColKey],
    });
  };

  const handleMouseMove = (e) => {
    if (!resizing || !tableRef.current) return;

    const deltaX = e.clientX - resizing.startX;
    const tableWidth = tableRef.current.offsetWidth;
    const deltaPercent = (deltaX / tableWidth) * 100;

    const newLeftWidth = resizing.startLeftWidth + deltaPercent;
    const newRightWidth = resizing.startRightWidth - deltaPercent;

    if (newLeftWidth >= 5 && newRightWidth >= 5) {
      setColumnWidths((prev) => ({
        ...prev,
        [resizing.leftCol]: newLeftWidth,
        [resizing.rightCol]: newRightWidth,
      }));
    }
  };

  const handleMouseUp = () => {
    setResizing(null);
  };

  const handleStatusIndicator = (status) => {
    switch (status) {
      case "ACTIVE":
        return "green";
      case "INACTIVE":
        return "orange";
      case "INITIAL":
        return "blue";
      default:
        return "blue";
    }
  };

  const handleStatusText = (status) => {
    switch (status) {
      case "ACTIVE":
        return texts.statusTextActive;
      case "INACTIVE":
        return texts.statusTextInactive;
      case "INITIAL":
        return texts.statusTextInitial;
      default:
        return "Unspecified";
    }
  };

  const handleClick = (user: ZitadelUser) => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: "editUserDrawerModal",
      }),
    );
    dispatch(updateCurrentlySelectedUserForEditUserDrawer(user));
  };

  const renderCellContent = (columnKey: string, user: ZitadelUser) => {
    switch (columnKey) {
      case "givenName":
        return user.given_name || "";
      case "familyName":
        return user.family_name || "";
      case "preferred_login_name":
        return user.preferred_login_name || "";
      case "email":
        return user.email || "";
      case "status":
        return (
          <IndicatorText
            text={handleStatusText(user.status)}
            variant={handleStatusIndicator(user.status)}
          />
        );
      case "roles":
        return Array.isArray(user.roles) ? user.roles.join(", ") : "";
      case "createdAt":
        return <TimezoneDate date={user.created_at} showRelative />;
      case "updatedAt":
        return <TimezoneDate date={user.updated_at} showRelative />;
      default:
        return "";
    }
  };

  useEffect(() => {
    if (resizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [resizing]);

  return (
    <div className={styles[`${cssClassName}__container`]}>
      <div className={styles[`${cssClassName}__wrapper`]}>
        {users.length === 0 ? (
          <div className={styles[`${cssClassName}__empty-wrapper`]}>
            <p className={styles[`${cssClassName}__empty-message`]}>
              {texts.noUsersFoundMessage}
            </p>
          </div>
        ) : (
          <div className={styles[`${cssClassName}__scroll`]}>
            <table ref={tableRef} className={styles[`${cssClassName}__table`]}>
              <UsersListHeader
                columns={columns}
                columnWidths={columnWidths}
                resizing={resizing}
                handleMouseDown={handleMouseDown}
              />
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className={styles[`${cssClassName}__row`]}
                    onClick={() => handleClick(user)}
                  >
                    {columns.map((column, index) => (
                      <td
                        key={column.key}
                        style={{ width: `${columnWidths[column.key]}%` }}
                        className={styles[`${cssClassName}__cell`]}
                      >
                        <div
                          className={styles[`${cssClassName}__cell-content`]}
                        >
                          {renderCellContent(column.key, user)}
                        </div>
                        {index < columns.length - 1 && (
                          <div
                            className={
                              styles[`${cssClassName}__cell-separator`]
                            }
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
